import { useMemo, useState } from 'react'
import { data, Link, useLoaderData } from 'react-router'
import {
	Accordion,
	AccordionContent,
	AccordionItem,
	AccordionTrigger,
} from '#app/components/ui/accordion.tsx'
import { Icon } from '#app/components/ui/icon.js'
import { getPublishablePages } from '#app/scoring/publishable-pages.server.ts'
import { SCORE_FACTORS } from '#app/scoring/scoring-model.ts'
import { type SeoProps } from '#app/seo/seo.tsx'
import { buildItemListLd } from '#app/seo/structured-data.ts'
import { categoryPlural } from '#app/utils/category-name.ts'
import { prisma } from '#app/utils/db.server.ts'
import { PAGE_MEASURE, PAGE_PROSE } from '#app/utils/layout.ts'
import { pluralize } from '#app/utils/misc.tsx'
import { type Route } from './+types/index.ts'

export async function loader({}: Route.LoaderArgs) {
	const categories = await prisma.category.findMany({
		where: {
			businesses: {
				some: {
					status: 'published',
				},
			},
		},
		select: {
			id: true,
			name: true,
			pluralName: true,
			slug: true,
			_count: {
				select: {
					businesses: {
						where: {
							status: 'published',
						},
					},
				},
			},
			subcategories: {
				where: {
					businesses: { some: { status: 'published' } },
				},
				select: {
					id: true,
					name: true,
					slug: true,
				},
				take: 3,
				orderBy: { name: 'asc' },
			},
		},
		orderBy: {
			name: 'asc',
		},
	})

	const totalProviders = await prisma.business.count({
		where: { status: 'published' },
	})

	/*
	 * This page is the fan-out point for the whole catalog, so it has to send
	 * each category to the URL that actually serves it. A category whose
	 * ranking publishes 301s from /categories/{slug} to /best/{slug}, and
	 * linking every card at the hub would route a large share of the site's
	 * internal links — and its crawl budget — through a redirect.
	 */
	const { bestCategories } = await getPublishablePages()
	const rankingSlugs = new Set(bestCategories.map((c) => c.slug))

	return data({
		categories: categories.map((category) => ({
			...category,
			ranked: rankingSlugs.has(category.slug),
			href: rankingSlugs.has(category.slug)
				? `/best/${category.slug}`
				: `/categories/${category.slug}`,
		})),
		totalProviders,
		// Two of the three numbers on this page. Stated rather than described,
		// because "many" and "a growing number" are the words a directory uses
		// when it will not say.
		rankedCount: rankingSlugs.size,
	})
}

// Breadcrumb handle for automatic breadcrumb generation
export const handle = {
	breadcrumb() {
		return { name: 'Categories', url: '/categories' }
	},
	seo({
		loaderData,
		origin,
		canonical,
	}: {
		loaderData: Awaited<ReturnType<typeof loader>>['data']
		origin: string
		canonical: string
	}): SeoProps {
		const structuredData =
			loaderData.categories.length > 0
				? [
						buildItemListLd({
							name: 'Business categories in Dallas-Fort Worth',
							// category.href, not a rebuilt /categories/ URL: half of these
							// now 301 to /best/, and structured data that names the
							// redirect describes a URL the reader never lands on.
							items: loaderData.categories.map((category) => ({
								name: category.name,
								url: `${origin}${category.href}`,
							})),
						}),
					]
				: undefined

		/*
		 * "Vetted" and "honest guidance" used to sit in all three of these.
		 * Neither is defensible: we read public reviews with software against a
		 * published rubric, and we do not interview anyone, inspect any work or
		 * verify any licence. The counts do the work those adjectives were
		 * standing in for, and unlike the adjectives they are true.
		 */
		const description = `Browse ${loaderData.categories.length} business categories across Dallas-Fort Worth, covering ${loaderData.totalProviders.toLocaleString()} listed businesses. Rankings are scored against a published rubric. Sponsored placements are labeled and excluded from them.`

		return {
			title: 'Business categories in Dallas-Fort Worth | DFW Professionals',
			description,
			canonical,
			openGraph: {
				title: 'Business categories in Dallas-Fort Worth | DFW Professionals',
				description,
				type: 'website',
				url: canonical,
			},
			twitter: {
				card: 'summary_large_image',
				title: 'Business categories in Dallas-Fort Worth | DFW Professionals',
				description,
			},
			structuredData,
		}
	},
}

export default function CategoriesPage() {
	const { categories, totalProviders, rankedCount } =
		useLoaderData<typeof loader>()

	return (
		<>
			{/* ═══════════════════════════════════════════════════════════════════
			    HERO SECTION
			    ═══════════════════════════════════════════════════════════════════ */}
			<section className="pt-16 pb-12 md:pt-24 md:pb-16">
				<div className={PAGE_MEASURE}>
					<h1 className="mb-4 font-serif text-3xl tracking-tight md:text-4xl">
						Business categories in Dallas-Fort Worth
					</h1>
					{/*
					 * Explains the one thing a reader cannot work out from the grid:
					 * why some entries read "Best X in DFW" and others just name the
					 * category. That distinction is the difference between a ranked
					 * page and a listing page, and leaving it unexplained made the
					 * grid look inconsistent rather than informative.
					 */}
					<p
						className={`text-muted-foreground mb-6 text-lg leading-relaxed ${PAGE_PROSE}`}
					>
						{rankedCount > 0 ? (
							<>
								{rankedCount} of these {categories.length} categories hold
								enough scored businesses to publish a ranking, and are linked
								below as <strong className="font-medium">Best</strong>. The rest
								link to a listing page while their corpus fills out.
							</>
						) : (
							<>
								Every category below links to the businesses listed in it.
								Rankings publish per category once enough of its businesses have
								been scored.
							</>
						)}
					</p>
					<div className="flex flex-wrap gap-4 text-sm">
						<div className="flex items-center gap-2">
							<Icon name="view-grid" className="text-muted-foreground size-4" />
							<span className="tabular-nums">
								{categories.length} categories
							</span>
						</div>
						<div className="flex items-center gap-2">
							<Icon name="check" className="size-4 text-green-600" />
							{/*
							 * "listed", not "researched" or "vetted". These are businesses
							 * we hold a record for. Most are not scored yet, and none has
							 * been interviewed or inspected by anyone.
							 */}
							<span className="tabular-nums">
								{totalProviders.toLocaleString()} businesses listed
							</span>
						</div>
					</div>
				</div>
			</section>

			{/* ═══════════════════════════════════════════════════════════════════
			    CATEGORY GRID
			    ═══════════════════════════════════════════════════════════════════ */}
			<section className="py-12 md:py-16">
				<div className={PAGE_MEASURE}>
					{categories.length === 0 ? (
						<div className="bg-muted rounded-lg py-16 text-center">
							<Icon
								name="file-text"
								className="text-muted-foreground mx-auto mb-4 size-12"
							/>
							<h3 className="mb-2 text-xl font-semibold">No Categories Yet</h3>
							<p className="text-muted-foreground">
								Categories will appear here once added.
							</p>
						</div>
					) : (
						<CategoryDirectory categories={categories} />
					)}
				</div>
			</section>

			{/* ═══════════════════════════════════════════════════════════════════
			    FAQ SECTION
			    ═══════════════════════════════════════════════════════════════════ */}
			{/*
			 * The band spans the viewport; its contents sit on the page measure.
			 * That is why this section keeps its own wrapper rather than moving
			 * inside a single page-level container.
			 */}
			<section className="bg-muted/30 border-y py-12 md:py-16">
				<div className={PAGE_MEASURE}>
					<div className={PAGE_PROSE}>
						<h2 className="mb-8 font-serif text-2xl tracking-tight">
							Common questions
						</h2>

						<Accordion type="single" collapsible className="w-full">
							<AccordionItem value="how-choose">
								<AccordionTrigger>
									How do you decide which businesses rank?
								</AccordionTrigger>
								<AccordionContent className="text-muted-foreground leading-relaxed">
									{/*
									 * The factor list is read from SCORE_FACTORS rather than
									 * written out, so this answer cannot drift from the rubric
									 * it describes. The previous version claimed we weigh
									 * "reputation" and "local track record", neither of which
									 * is a factor, and framed it as human judgment.
									 */}
									<p className="mb-2">
										Software reads the public Google reviews a business has
										already received and scores them against{' '}
										{SCORE_FACTORS.length} published, weighted factors:{' '}
										{SCORE_FACTORS.map((factor) => factor.label.toLowerCase())
											.join(', ')
											.replace(/, ([^,]*)$/, ' and $1')}
										. The heaviest is what reviewers wrote, not the star they
										clicked.
									</p>
									<p>
										Nobody is interviewed, no job site is inspected and no
										licence is verified. The full rubric and the weight on each
										factor are at{' '}
										<Link
											className="underline underline-offset-4"
											to="/scoring-method"
										>
											how we score
										</Link>
										.
									</p>
								</AccordionContent>
							</AccordionItem>

							<AccordionItem value="paid-placement">
								<AccordionTrigger>
									Can a business pay to rank higher?
								</AccordionTrigger>
								<AccordionContent className="text-muted-foreground leading-relaxed">
									No. Rank is computed from the rubric and nothing else.
									Sponsored placements, where they exist, are labeled as
									sponsored and excluded from the rankings entirely.
								</AccordionContent>
							</AccordionItem>

							<AccordionItem value="all-categories">
								<AccordionTrigger>What do you cover?</AccordionTrigger>
								<AccordionContent className="text-muted-foreground leading-relaxed">
									{/*
									 * Derived, not enumerated. The old answer listed
									 * "contractors and plumbers, lawyers, accountants,
									 * dentists", which describes whichever verticals happened
									 * to be seeded when it was written and quietly goes stale.
									 */}
									{categories.length} categories across Dallas-Fort Worth,
									covering {totalProviders.toLocaleString()} listed businesses.
									Every category with at least one published business appears in
									the list above.
								</AccordionContent>
							</AccordionItem>

							<AccordionItem value="not-listed">
								<AccordionTrigger>
									What if my category isn't listed?
								</AccordionTrigger>
								<AccordionContent className="text-muted-foreground leading-relaxed">
									A category appears here once it holds at least one published
									business, so the fastest way to add one is to{' '}
									<Link className="underline underline-offset-4" to="/submit">
										submit a business
									</Link>{' '}
									in it.
								</AccordionContent>
							</AccordionItem>

							<AccordionItem value="city-specific">
								<AccordionTrigger>
									Are rankings the same across every DFW city?
								</AccordionTrigger>
								<AccordionContent className="text-muted-foreground leading-relaxed">
									No. Each city and category pair gets its own page, scored over
									the businesses that serve that city, so the order differs from
									the metro-wide ranking. Start from{' '}
									<Link className="underline underline-offset-4" to="/cities">
										the city index
									</Link>{' '}
									if you know where you are.
								</AccordionContent>
							</AccordionItem>
						</Accordion>
					</div>
				</div>
			</section>

			{/* ═══════════════════════════════════════════════════════════════════
			    BROWSE BY CITY CTA
			    ═══════════════════════════════════════════════════════════════════ */}
			<section className="py-12 md:py-16">
				<div className={PAGE_MEASURE}>
					<h2 className="mb-4 font-serif text-xl tracking-tight">
						Know your city? Start there instead.
					</h2>
					<p className="text-muted-foreground mb-6">
						City pages are scored over the businesses that serve that city, so
						they rank differently from the metro-wide list.
					</p>
					<Link
						to="/cities"
						className="text-primary inline-flex items-center gap-2 font-medium hover:underline"
					>
						Browse by city
						<Icon name="arrow-right" className="size-4" />
					</Link>
				</div>
			</section>
		</>
	)
}

/**
 * The catalog's fan-out point.
 *
 * At ~106 categories a flat card grid is a wall, which is exactly why this
 * could never have been a header dropdown: the list has to be filterable and
 * sectioned to be usable, and neither works inside a menu.
 *
 * Filtering is client-side over data the loader already sent. The full list
 * stays in the server-rendered HTML, so a crawler or a model reading this page
 * sees every category and every link regardless of the filter — the input is a
 * reader convenience layered on top of a complete document, not a gate in
 * front of one.
 *
 * Alphabetical sections rather than invented groupings. Grouping 106
 * categories into "Home Services", "Health" and so on is a real improvement
 * but needs a taxonomy nobody has decided yet; A–Z is correct today and does
 * not have to be unpicked later.
 */
function CategoryDirectory({
	categories,
}: {
	categories: Array<{
		id: string
		name: string
		/** See `Category.pluralName`; null means the ordinary rule is correct. */
		pluralName: string | null
		slug: string
		href: string
		ranked: boolean
		_count: { businesses: number }
		subcategories: Array<{ id: string; name: string; slug: string }>
	}>
}) {
	const [filter, setFilter] = useState('')

	const sections = useMemo(() => {
		const needle = filter.trim().toLowerCase()
		const matches = needle
			? categories.filter(
					(category) =>
						category.name.toLowerCase().includes(needle) ||
						category.subcategories.some((sub) =>
							sub.name.toLowerCase().includes(needle),
						),
				)
			: categories

		const byLetter = new Map<string, typeof matches>()
		for (const category of matches) {
			// Anything not starting with a letter groups under # rather than
			// creating a section per punctuation mark.
			const initial = category.name.charAt(0).toUpperCase()
			const letter = /[A-Z]/.test(initial) ? initial : '#'
			const bucket = byLetter.get(letter)
			if (bucket) bucket.push(category)
			else byLetter.set(letter, [category])
		}

		return [...byLetter.entries()].sort(([a], [b]) => a.localeCompare(b))
	}, [categories, filter])

	const shown = sections.reduce((sum, [, items]) => sum + items.length, 0)

	return (
		<div>
			<div className="mb-8">
				<label htmlFor="category-filter" className="sr-only">
					Filter categories
				</label>
				<div className="relative">
					<Icon
						name="magnifying-glass"
						className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2"
					/>
					<input
						id="category-filter"
						type="search"
						value={filter}
						onChange={(event) => setFilter(event.target.value)}
						placeholder={`Filter ${categories.length} categories`}
						className="border-input bg-background focus-visible:ring-ring h-11 w-full rounded-md border py-2 pr-4 pl-9 text-sm focus-visible:ring-1 focus-visible:outline-none"
					/>
				</div>
				{filter ? (
					<p className="text-muted-foreground mt-2 text-sm" aria-live="polite">
						{shown} of {categories.length} categories
					</p>
				) : null}
			</div>

			{shown === 0 ? (
				<p className="text-muted-foreground py-8 text-center">
					No category matches “{filter}”.{' '}
					<button
						type="button"
						className="underline underline-offset-4"
						onClick={() => setFilter('')}
					>
						Clear the filter
					</button>
					.
				</p>
			) : null}

			<div className="space-y-10">
				{sections.map(([letter, items]) => (
					<section key={letter}>
						<h3 className="text-muted-foreground mb-3 text-sm font-medium tracking-widest">
							{letter}
						</h3>
						<ul className="grid gap-x-8 gap-y-1 sm:grid-cols-2">
							{items.map((category) => (
								<li key={category.id} className="border-b py-3">
									<div className="flex items-baseline justify-between gap-3">
										{/*
										 * Anchor text carries the query, not just the noun.
										 * "Best Roofing Contractors in DFW" is what people search
										 * and what a model quotes; "Roofing Contractor" tells a
										 * crawler far less about the destination.
										 */}
										<Link
											to={category.href}
											className="font-medium hover:underline"
										>
											{category.ranked
												? `Best ${categoryPlural(category)} in DFW`
												: categoryPlural(category)}
										</Link>
										<span className="text-muted-foreground shrink-0 text-xs tabular-nums">
											{category._count.businesses}
										</span>
									</div>
									{category.subcategories.length > 0 ? (
										// Real links, not chips. These were styled spans, which
										// made the hub's deepest fan-out invisible to crawlers.
										<div className="text-muted-foreground mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs">
											{category.subcategories.map((sub) => (
												<Link
													key={sub.id}
													to={`/categories/${sub.slug}`}
													className="hover:text-foreground hover:underline"
												>
													{sub.name}
												</Link>
											))}
										</div>
									) : null}
								</li>
							))}
						</ul>
					</section>
				))}
			</div>
		</div>
	)
}
